/**
 * Global authentication interceptor
 * Handles token expiration and 401 errors across the entire app
 */

import { supabase } from "@/integrations/supabase/client";

let isRedirecting = false;

/**
 * Initialize global auth interceptor
 * This should be called once in App.tsx
 */
export function initializeAuthInterceptor() {
  // Listen for auth state changes globally
  const { data: { subscription } } = supabase.auth.onAuthStateChange(
    async (event, session) => {
      // Handle token expiration and sign out
      if (event === "SIGNED_OUT" || (!session?.user && event !== "SIGNED_IN")) {
        // Check if we're on a protected route
        const currentPath = window.location.pathname;
        const protectedRoutes = ["/dashboard", "/onboarding"];
        const isProtected = protectedRoutes.some(route => currentPath.startsWith(route));

        if (isProtected && !isRedirecting) {
          isRedirecting = true;
          
          // Store current path for redirect after login
          if (currentPath !== "/auth" && currentPath !== "/") {
            sessionStorage.setItem("redirect_after_auth", currentPath + window.location.search);
          }
          
          // Redirect to auth page
          window.location.href = "/auth";
        }
      }

      // Reset redirect flag when user signs in
      if (event === "SIGNED_IN" && session?.user) {
        isRedirecting = false;
      }
    }
  );

  // Intercept Supabase client requests to handle 401 errors
  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    
    // Check for 401 Unauthorized responses
    if (response.status === 401) {
      const url = args[0];
      const urlString = typeof url === "string" ? url : url?.toString() || "";
      
      // Only handle Supabase API calls (check for Supabase URL patterns)
      // Supabase API calls typically include /rest/v1/ or /auth/v1/
      const isSupabaseCall = urlString.includes("/rest/v1/") || 
                            urlString.includes("/auth/v1/") ||
                            urlString.includes("/storage/v1/") ||
                            urlString.includes("/realtime/v1/");
      
      if (isSupabaseCall) {
        // Check current session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session?.user && !isRedirecting) {
          isRedirecting = true;
          
          const currentPath = window.location.pathname;
          const protectedRoutes = ["/dashboard", "/onboarding"];
          const isProtected = protectedRoutes.some(route => currentPath.startsWith(route));

          if (isProtected) {
            // Store current path for redirect after login
            if (currentPath !== "/auth" && currentPath !== "/") {
              sessionStorage.setItem("redirect_after_auth", currentPath + window.location.search);
            }
            
            // Redirect to auth page
            window.location.href = "/auth";
          }
        }
      }
    }
    
    return response;
  };

  return () => {
    subscription.unsubscribe();
    window.fetch = originalFetch;
  };
}

/**
 * Check if user is authenticated
 * Returns true if user has valid session, false otherwise
 */
export async function checkAuth(): Promise<boolean> {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error("Auth check error:", error);
      return false;
    }
    
    return !!session?.user;
  } catch (error) {
    console.error("Error checking auth:", error);
    return false;
  }
}

